import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { StrHlp } from 'src/lib/fliko/helpers/string.helper';
import { ReplaceMap } from 'src/lib/fliko/interfaces/replaceMap';
import { SubmitEstateQrDefectData } from 'src/lib/fliko/models/http/estateQrDefect.data';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { EstateQrRouteData } from 'src/app/view/estate-view/helpers/estate-qr.helper';
import { toLocalizedSummaryDetail } from '../../helpers/public-defect-status.helper';
import { createEstateQrPhoto, getEstateQrPhotoBytes, isAcceptedEstateQrPhotoType } from './estate-qr-page.photos';
import { EstateQrMessage, EstateQrPhoto } from './estate-qr-page.models';

export type EstateQrReportFormValue = {
  description: string | null;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  notifyUpdates: boolean | null;
  privacyConsent: boolean | null;
};

export function createEstateQrReportForm(fb: FormBuilder): FormGroup {
  return fb.group({
    description: ['', [trimmedRequiredValidator, Validators.maxLength(1000)]],
    firstName: [''],
    lastName: [''],
    email: ['', [Validators.required, Validators.email]],
    phone: [''],
    notifyUpdates: [true],
    privacyConsent: [false, Validators.requiredTrue]
  });
}

export function mergeEstateQrDescriptionValue(currentValue: unknown, transcript: string): string {
  const current =
    typeof currentValue === 'string' ? currentValue.trim() : currentValue != null ? String(currentValue).trim() : '';
  return current ? `${current} ${transcript}` : transcript;
}

export function buildEstateQrPropertyDetailsSummary(
  qrData: EstateQrRouteData | null,
  qrLocationTypeLabel: string | null,
  ls: LocalizationService
): string {
  return [
    toLocalizedSummaryDetail('navigation.estate', qrData?.estateName, ls),
    toLocalizedSummaryDetail('estate.qrPublicLocationType', qrLocationTypeLabel, ls),
    toLocalizedSummaryDetail('addr.blockNo', qrData?.buildingNumber, ls),
    toLocalizedSummaryDetail('addr.stairNo', qrData?.stairwayNumber, ls),
    toLocalizedSummaryDetail('addr.floor', qrData?.floor, ls),
    toLocalizedSummaryDetail('addr.number', qrData?.number, ls)
  ]
    .filter((detail): detail is string => !!detail)
    .join(' • ');
}

export async function appendEstateQrPhotos(
  currentPhotos: EstateQrPhoto[],
  selectedFiles: File[],
  maxPhotoCount: number,
  maxSinglePhotoBytes: number,
  maxTotalPhotoBytes: number
): Promise<{ nextPhotos: EstateQrPhoto[]; errorMessage: EstateQrMessage | null }> {
  const errors: EstateQrMessage[] = [];
  const validFiles = selectedFiles.filter((file) => isAcceptedEstateQrPhotoType(file));
  const availableSlots = Math.max(maxPhotoCount - currentPhotos.length, 0);
  const filesToAdd = validFiles.slice(0, availableSlots);
  const nextPhotos = [...currentPhotos];

  if (filesToAdd.length) {
    const mappedFiles = await Promise.all(filesToAdd.map((file) => createEstateQrPhoto(file)));
    let totalSize = getEstateQrPhotoBytes(currentPhotos);

    mappedFiles.forEach((photo) => {
      if (photo.file.size > maxSinglePhotoBytes) {
        errors.push(createEstateQrMessage('estate.qrPublicPhotoSingleSizeError'));
        return;
      }

      if (totalSize + photo.file.size > maxTotalPhotoBytes) {
        errors.push(createEstateQrMessage('estate.qrPublicPhotoTotalSizeError'));
        return;
      }

      totalSize += photo.file.size;
      nextPhotos.push(photo);
    });
  }

  if (selectedFiles.length !== validFiles.length) {
    errors.push(createEstateQrMessage('estate.qrPublicPhotoFormatError'));
  }
  if (validFiles.length > availableSlots) {
    errors.push(createEstateQrMessage('estate.qrPublicPhotoMaxError', { $max: maxPhotoCount }));
  }

  return {
    nextPhotos,
    errorMessage: errors[0] ?? null
  };
}

export function buildEstateQrSubmitPayload(
  formValue: EstateQrReportFormValue,
  qrData: EstateQrRouteData,
  photos: EstateQrPhoto[]
): SubmitEstateQrDefectData {
  const qrCodeId = StrHlp.trimToNull(qrData.qrCodeId);

  return {
    estateId: qrData.estateId as string,
    ...(qrCodeId ? { qrCodeId } : {}),
    description: formValue.description?.trim() ?? '',
    firstName: StrHlp.trimToNull(formValue.firstName),
    lastName: StrHlp.trimToNull(formValue.lastName),
    email: formValue.email?.trim() ?? '',
    phone: StrHlp.trimToNull(formValue.phone),
    notifyUpdates: formValue.notifyUpdates !== false,
    privacyConsent: formValue.privacyConsent === true,
    photos: photos.map((photo) => ({
      fileName: photo.file.name,
      dataUrl: photo.previewDataUrl
    }))
  };
}

export function createEstateQrMessage(key: string, replaceMap?: ReplaceMap): EstateQrMessage {
  return { key, replaceMap };
}

export function getEstateQrSubmitErrorMessage(
  publicStatusCode: string | null | undefined,
  errorCode: string | null | undefined = null
): EstateQrMessage {
  if (publicStatusCode === 'rate-limit-exceeded' || errorCode === 'functions/resource-exhausted') {
    return createEstateQrMessage('estate.qrPublicRateLimitError');
  }

  return createEstateQrMessage('estate.qrPublicSubmitError');
}

export function localizeEstateQrMessage(message: EstateQrMessage | null, ls: LocalizationService): string | null {
  return message ? ls.trReplace(message.key, message.replaceMap) : null;
}

function trimmedRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return StrHlp.trimToNull(typeof control.value === 'string' ? control.value : null) ? null : { required: true };
}
